import { FinancingInput } from '@oneaudi/onegraph-client';
import { ProgressIndicator } from '@oneaudi/unified-web-alpha-components';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FsagFormattedPreset, ResponseFromCalculator } from '~/@types/calculator';
import { useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import { Dialog } from '~/components/dialog';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { state } from '~/storage';
import { Webcalc } from '~/app/compositions/content/calculation-info/styles/calculation-info-style';

export function FsagMonthlyPriceRateDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		if (open) {
			appTrackingService.updateLayerComponent(LayerType.FinancingOptionsLayer, 'open');
			appTrackingService.viewChangeEvent({
				eventName: `configurator - ${LayerType.FinancingOptionsLayer}`,
				componentName: 'configurator-financing-options-layer',
			});
		}
	}, [open]);

	const trackDialogClose = useCallback(() => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (${LayerType.FinancingOptionsLayer}) - click to close layer`,
			componentName: 'configurator-financing-options-layer',
		});
		appTrackingService.updateLayerComponent(LayerType.FinancingOptionsLayer, 'close');

		appTrackingService.viewChangeEvent({
			eventName: 'generic',
		});
	}, []);
	return (
		<Dialog
			open={open}
			onCloseButtonClick={() => {
				trackDialogClose();
				onClose();
			}}>
			<FsagFinanceDialog onClose={onClose} open={open} />
		</Dialog>
	);
}

const FsagFinanceDialog = ({ onClose, open }: DialogProps) => {
	const { i18n } = useI18n();
	const { config, setFinancingParameter, language, market } = useMarketConfig();
	const [preset, setPreset] = useAtom(state.fsagFinancialCalculatorSettings);
	const [loading, setLoading] = useState<boolean>(true);
	const { financialCalculator, carIdentifier } = useConfigurator();
	const culture = `${language}-${market.toUpperCase()}`;
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { financingData } = useAppState();
	const [hasFinancingChanged, setHasFinancingChanged] = useAtom(state.hasFinancingChanged);

	// Memoize the calculator instance to prevent recreation on every render
	const calculator = useMemo(() => {
		if (!financialCalculator) return null;
		return isConsFinancingStage()
			? new financialCalculator({
					WEBCALC_API_URL: 'https://api.cons.webcalc.vwfs.io/webcalc-frontend-service',
					WEBCALC_API_STAGE: 'cons',
				})
			: new financialCalculator();
	}, [financialCalculator]);

	// Memoize the request object to prevent recreation on every render
	const request = useMemo(() => {
		if (!financingData?.financing) return null;
		const product = preset ?? { '@ID': defaultProduct };
		return {
			Request: {
				'@Domain': financingData?.financing.meta?.externalRequestOptions['@Domain']
					? financingData?.financing.meta?.externalRequestOptions['@Domain']
					: config.financeDomain,
				'@Culture': culture,
				Vehicle: {
					...financingData?.financing.meta?.externalRequestOptions?.Vehicle,
				},
				Product: product,
			},
		};
	}, [preset, defaultProduct, config.financeDomain, culture, financingData?.financing, carIdentifier]);

	const layout =
		market === 'NL'
			? {
					LeftColumnRatio: '3/5',
					RightColumnRatio: '2/5',
					MultiColumn: true,
				}
			: { MultiColumn: false };

	const getSelectedFinanceProduct = useCallback((selectedProduct: ResponseFromCalculator) => {
		if (!selectedProduct) {
			return { product: 'PL', productParameters: [] };
		}

		return {
			product: selectedProduct['@ID'],
			productParameters: selectedProduct.Parameter?.map((p) => ({ key: p['@ID'], value: p['#text'] })) ?? [],
		};
	}, []);

	const updateFinancingInput = useCallback(
		(updatedFinancingInput: FinancingInput) => {
			setFinancingParameter(updatedFinancingInput);
			onClose();
		},
		[setFinancingParameter, onClose],
	);

	useEffect(() => {
		if (hasFinancingChanged) {
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (financing options layer) - click to accept financing`,
				componentName: 'configurator-financing-options-layer',
				label: i18n('ngc.conflict.accept'),
				elementName: 'button',
			});

			appTrackingService.updateLayerComponent(LayerType.FinancingOptionsLayer, 'close');

			appTrackingService.viewChangeEvent({
				eventName: 'generic',
			});
			appTrackingService.priceUpdate({ value: 'financing accepted' });

			setHasFinancingChanged(false);
		}
	}, [hasFinancingChanged, i18n]);

	useEffect(() => {
		if (!open || financingData?.loading || !calculator || !request) return;

		const areaForCalc = document.getElementById('finCalc');
		if (!areaForCalc) return;

		calculator
			.getInterface(request, {
				ResultContainer: areaForCalc,
				LayoutConfig: layout,
				ApplyButtonFunction: (currentParameters: FsagFormattedPreset) => {
					setPreset(currentParameters);
					updateFinancingInput(getSelectedFinanceProduct(currentParameters as ResponseFromCalculator));
				},
				ApplyButton: i18n('ngc.conflict.accept'),
				AutoPostback: true,
				ContainerStyle: {
					padding: 'grid1',
				},
				ErrorCallback: () => {
					console.error('Financial error Callback triggered.');
				},
				FatalErrorMessage: i18n('ngc.webcalc.error.fatal'),
			})
			.then(() => {
				setLoading(false);
			})
			.catch((error: Record<string, unknown>) => {
				console.error('Unexpected issue using Financial Calculator: ', error);
			});

		return () => {
			setLoading(false);
		};
	}, [
		open,
		financingData?.loading,
		calculator,
		request,
		i18n,
		setPreset,
		updateFinancingInput,
		getSelectedFinanceProduct,
	]);

	return (
		<Webcalc>
			{loading && financialCalculator && <ProgressIndicator type={'infinite'} variant={'spinner'} />}
			<div id="finCalc" />
		</Webcalc>
	);
};

export default FsagFinanceDialog;
